import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';

interface HotelPricingAssistantPmsSsConnectPmsButtonSignature {
  Args: {
    redirectToPmsIntegrationSettings?: boolean;
  };
}

export default class HotelPricingAssistantPmsSsConnectPmsButton extends Component<HotelPricingAssistantPmsSsConnectPmsButtonSignature> {
  @service declare access: Services['access'];
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service declare pricingAssistantPmsSs: Services['pricing-assistant-pms-ss'];
  @service declare router: Services['router'];

  get isRequestInProgress(): boolean {
    return this.pricingAssistantPmsSs.launchConnectPmsRequestTask.isRunning;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get redirectToPmsIntegrationSettings() {
    return this.args.redirectToPmsIntegrationSettings ?? false;
  }

  @action
  async onConnectPMSButtonClicked(): Promise<void> {
    // Only launch the connectPmsRequest in case the user has PA settings edit rights (otherwise BE will block it anyway)
    // For now users without edit rights can click the Connect PMS button, but they will just be redirected to the PMS integrations page.
    // They'll see an info message with some explanation there.
    if (this.access.hasPricingAssistantSettingsEditRights) {
      const result =
        await this.pricingAssistantPmsSs.launchConnectPmsRequestTask.perform();

      if (result.isSuccess) {
        // Update feature flags for changed in-app states
        await this.access.refetchFeatureFlags();

        if (this.redirectToPmsIntegrationSettings) {
          // Do page redirect if needed (Connect button clicked on other place than the PMS Integrations page)
          this.router.transitionTo('hotel.settings.pms-integration');
        }
      } else {
        this.errorTracking.captureMessage(
          'Pricing Assistant Connect PMS handling failed',
          {
            error: result.error,
          },
        );

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();
      }
    } else if (this.redirectToPmsIntegrationSettings) {
      this.router.transitionTo('hotel.settings.pms-integration');
    }
  }

  <template>
    <PrismButton
      @buttonType="brand"
      @isDisabled={{this.isRequestInProgress}}
      @label={{t "pricing_assistant.self_service.banner.action.connect_pms"}}
      @onClick={{this.onConnectPMSButtonClicked}}
      data-test-id="pms-ss-connect-pms-button"
    />
  </template>
}
